import React from 'react';
import { Box, Stack, IconButton, Button, Typography, styled, Tooltip } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { MentionsInput, Mention } from 'react-mentions';
import { Formik, Form } from 'formik';
import LocalStorage from '../../defined/localStorage';
import { translate } from '../../helper';
import { useSelector } from 'react-redux';
import Utils from '../../utils';
import { v4 as uuidv4 } from 'uuid';
import { Colors } from '../../themes/Colors';
import { processImageFile } from '../../utils/imageProcessor';
import CustomSpinner from '../CustomSpinner';
import { resolveCssVarValue } from '../../utils/CommonFunction';

const ComposerWrapper = styled(Box)(({ theme, composerDisabled }) => ({
  border: '1px solid #e0e0e0',
  borderRadius: 'calc(var(--sp-xxs) * 2)',
  padding: 'calc(var(--sp-xxs)*2)',
  transition: 'height 150ms ease, max-height 150ms ease',
  backgroundColor: composerDisabled ? Colors.gray6 : '#fff',
  transition: 'border-color 150ms',
  '&.dragover': {
    borderColor: '#F8B218',
    backgroundColor: '#FFFBEA',
  },
  '&:focus-within': {
    borderColor: '#1976d2',
  },
}));

const baseMentionInputStyle = {
  control: { backgroundColor: '#fff', fontSize: 'calc(var(--fs-xs)*1.6)' },
  '&multiLine': {
    control: { minHeight: 'calc(var(--fs-hero)*1.4)', maxHeight: 'calc(var(--fs-hero)*4.5)', overflowY: 'auto' },
    highlighter: { padding: 'calc(var(--sp-xs)*1.8)', border: '1px solid transparent' },
    input: {
      padding: 'calc(var(--sp-xs)*1.8)',
      border: 'none',
      // border: '1px solid silver',
      // borderRadius: 8,
      transition: 'max-height 150ms ease',
    },
  },
  suggestions: {
    position: 'absolute',
    top: 'calc(var(--sp-lg)*1)',
    left: 'calc(var(--sp-lg)*2)',
    zIndex: 5,
    minWidth: 'calc(var(--fs-hero)*6.603)',
    maxHeight: 'calc(var(--fs-hero)*6)',
    overflowY: 'auto',
    list: {
      backgroundColor: 'white',
      border: '1px solid rgba(0,0,0,0.15)',
      fontSize: 'var(--fs-md2)'
    },
    item: {
      padding: 'calc(var(--sp-xs)*1) calc(var(--sp-md2)*1.4)',
      fontSize: 'var(--fs-md2)',
      lineHeight: 1.4,
      borderBottom: '1px solid rgba(0,0,0,0.15)',
      '&focused': { backgroundColor: '#cee4e5' },
    },
  },
};
const getMentionInputStyle = (composerDisabled, inputMinHeight, inputMaxHeight) =>
  composerDisabled
    ? {
        ...baseMentionInputStyle,
        control: { ...baseMentionInputStyle.control, backgroundColor: Colors.gray6 },
        '&multiLine': {
          ...baseMentionInputStyle['&multiLine'],
          control: {
            ...baseMentionInputStyle['&multiLine'].control,
            minHeight: inputMinHeight,
            maxHeight: inputMaxHeight,
            backgroundColor: Colors.gray6,
          },
        },
      }
    : {
        ...baseMentionInputStyle,
        '&multiLine': {
          ...baseMentionInputStyle['&multiLine'],
          control: {
            ...baseMentionInputStyle['&multiLine'].control,
            minHeight: inputMinHeight,
            maxHeight: inputMaxHeight,
          },
        },
      };
const brand = JSON.parse(LocalStorage.getItem('branding'));
const SendButton = styled(Button)(({ }) => {
  const whiteLabel = useSelector((state) => state.User?.branding);
  return ({
    background: `linear-gradient(222.6deg, ${brand?.web_button_color || whiteLabel?.web_button_color || '#F8B218'} 0%, ${brand?.web_button_color || whiteLabel?.web_button_color || '#F8B218'} 100%)`,
    borderRadius: 'var(--sp-xs)',
    boxShadow: '0px 4px 10px 0px #B9830940',
    color: 'white !important',
    padding:'var(--sp-md2) var(--sp-xl) !important',
    marginLeft: 'var(--sp-md2)',
    fontWeight: 700,
    fontSize: 'var(--fs-md2)',
    border: '0 !important',
    outline: 'none',
  })
});
const mentionStyle = { backgroundColor: '#cee4e5' };

export default function MessageComposer({
  mentionsData = [],
  placeholder = 'Write a message…',
  maxImages = 5,
  unlimited = false,
  onSubmit = () => { },
  inputMinHeight = 'calc(var(--fs-hero)*2.5)',
  inputMaxHeight = 'calc(var(--fs-hero)*6)',
  btnText = 'send',
  validationSchema,
  uploadIdKey = "order_id",
  uploadIdValue,
  isMobile,
  files,
  setFiles,
  uploadControllers,
  disableUpload = false,
  prevValue = '',
  composerDisabled = false,
  allowEmptySubmit = false,

}) {
  const [attachments, setAttachments] = React.useState([]);
  const fileInputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  const [processing, setProcessing] = React.useState(false);
  const mentionInputStyle = getMentionInputStyle(composerDisabled, inputMinHeight, inputMaxHeight);
  React.useEffect(() => {
    const seeded = files.filter(f => (f.fromServer));
    if (seeded.length === 0) return;
    setAttachments((prev) => {
      const prevPaths = new Set(prev.map(p => p.path));
      const newOnes = seeded
        .filter(s => s.path && !prevPaths.has(s.path))
        .map(s => ({
          file_name: s.file_name ,
          "content-type": s["content-type"] || '',
          path: s.path,
        }));
      if (newOnes.length === 0) return prev;
      return [...prev, ...newOnes];
    });
  }, [files]);

  const handlePickFiles = async (e) => {
    const allowedTypes = {
      "application/pdf": "pdf",
      "image/jpeg": "jpeg",
      "image/jpg": "jpg",
      "image/png": "png",
      "image/gif": "gif",
      "image/webp": "webp",
      "image/heic": "heic",
      "image/heif": "heif",
    };
    const maxImageSize = 20 * 1024 * 1024;
    const maxPdfSize = 50 * 1024 * 1024;
    const chosen = Array.from(e.target.files || []);

    const validFiles = chosen.filter((file) => {
      if (!allowedTypes[file.type]) {
        Utils.showAlert(2, `File type not allowed: ${file.name}`);
        return false;
      }
      if (file.type === "application/pdf" && file.size > maxPdfSize) {
        Utils.showAlert(2, `PDF size exceeds 50MB: ${file.name}`);
        return false;
      }
      if (file.type.startsWith("image/") && file.size > maxImageSize) {
        Utils.showAlert(2, `Image size exceeds 20MB: ${file.name}`);
        return false;
      }
      return true;
    });
    if (!unlimited && files.length + chosen.length > maxImages) {
      Utils.showAlert(2, `You can only upload a maximum of ${maxImages} files.`);
      e.target.value = '';
      return;
    }
    if (validFiles.length === 0) {
      e.target.value = '';
      return;
    }

    if(validFiles.filter(f=>f.type==="image/heic" || f.type==="image/heif").length>0){
      setProcessing(true);
    }

    const processedFiles = (
      await Promise.all(validFiles.map((file) => processImageFile(file)))
    ).filter(Boolean);

    const itemsToAdd = processedFiles.map((file) => ({
      id: uuidv4(),
      file,
      url: null,
      previewUrl: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      uploading: true,
    }));
    setFiles((prev) => [...prev, ...itemsToAdd]);
    setProcessing(false);

    const contentTypes = processedFiles.map((file) => file.type);
    try {
      const payload = { [uploadIdKey]: uploadIdValue, content_type: contentTypes };
      const response = await new Promise((resolve, reject) => {
        Utils.api.postApiCall(
          Utils.EndPoint.vendorEndpoints.uploadFileApi,
          payload,
          (res) => resolve(res.data.upload_urls),
          (err) => reject(err)
        );
      });

      const uploadUrls = response;
      const uploadPromises = [];
      for (let i = 0; i < processedFiles.length; i++) {
        const file = processedFiles[i];
        const controller = new AbortController();
        uploadControllers.current.push(controller);

        const uploadPromise = fetch(uploadUrls[i].upload_url, {
          method: 'PUT',
          body: file,
          headers: { 'x-ms-blob-type': 'BlockBlob' },
          signal: controller.signal,
        })
          .then(uploadedRes => {
            if (uploadedRes.status === 201) {
              const itemId = itemsToAdd[i].id;
              setFiles((prev) =>
                prev.map((item) =>
                  item.id === itemId
                    ? {
                        ...item,
                        uploading: false,
                      }
                    : item
                )
              );
              setAttachments((prev) => [
                ...prev,
                {
                  file_name: file.name,
                  "content-type": uploadUrls[i].content_type,
                  path: uploadUrls[i].file_path
                }
              ]);
              Utils.showAlert(1, `${file.name} uploaded successfully`);
            } else {
              Utils.showAlert(2, `Upload failed with status ${uploadedRes.status}`);
              setFiles((prev) =>
                prev.filter((item) => !(item.file.name === file.name && item.file.size === file.size))
              );
            }
          })
          .catch(error => {
            if (error.name === 'AbortError') {
              // Upload was cancelled, do nothing
            } else {
              Utils.showAlert(2, `Uploading failed for file: ${file.name}. Error: ${error.message}`);
            }
          });

        uploadPromises.push(uploadPromise);
      }

      await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error fetching upload URLs:', error);
      Utils.showAlert(2, `Failed to fetch upload URLs. Please try again.`);
      setFiles((prev) => prev.filter((item) => !item.uploading));
      setProcessing(false);
    }

    e.target.value = '';
  };

  const removeFileAt = (idx) => {
        setFiles((prev) => {
          const toRemove = prev[idx];
          if (toRemove) {
            if (toRemove.url) URL.revokeObjectURL(toRemove.url);
            if (toRemove.previewUrl) URL.revokeObjectURL(toRemove.previewUrl);
          }

          setAttachments((attPrev) => {
            if (toRemove && toRemove.path) {
              return attPrev.filter((a) => a.path !== toRemove.path);
            }
            return attPrev.filter((_, i) => i !== idx);
          });

          return prev.filter((_, i) => i !== idx);
        });
      };

  React.useEffect(() => {
    return () => {
      files.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
        if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
      });
    };
  }, [files]);

  React.useEffect(() => {
    if ((files?.length || 0) === 0 && attachments.length > 0) {
      setAttachments([]);
    }
  }, [files?.length]);

  const handleMentionsChange = (setValues) => (evt, newValue, _newPlainTextValue, taggedUsers) => {
    const userIds = [...new Set(taggedUsers.map((user) => Number(user.id)))];
    setValues((prev) => ({
      ...prev,
      text: newValue,
      tagged_users: userIds,
    }));
  };
  const handleSubmit = (values, actions) => {
    onSubmit(values, attachments, actions);
  };
  const anyUploading = files.some(item => item.uploading);

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragOver(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disableUpload) {
      Utils.showAlert(2, 'Unable to Upload please select Customer PO first');
      return
    } else if(composerDisabled) {
      return
    }
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) {
      handlePickFiles({ target: { files } });
    }
  };
  const handlePaste = (e) => {
    if (disableUpload || composerDisabled) return;
    const items = e.clipboardData?.items || [];
    const files = [];
    for (let i = 0; i < items.length; i++) {
      if (items[i].kind === 'file') {
        const file = items[i].getAsFile();
        if (file) files.push(file);
      }
    }
    if (files.length) {
      handlePickFiles({ target: { files } });
      e.preventDefault();
    }
  };

  return (
    <Formik
      initialValues={{ text: prevValue, taggedUsers: [] }}
      onSubmit={handleSubmit}
      validationSchema={allowEmptySubmit ? undefined : validationSchema}
    >
      {({ isSubmitting, values, errors, touched, setValues }) => (
        <Form>
          <ComposerWrapper
            className={dragOver ? 'dragover' : ''}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onPaste={handlePaste}
            style={{ position: 'relative' }}
            composerDisabled={composerDisabled}
          >
            <Stack spacing={1}>
              <MentionsInput
                className="mentions-input"
                value={values.text}
                placeholder={placeholder}
                error={Boolean(touched.text && errors.text)}
                onChange={handleMentionsChange(setValues)}
                readOnly={composerDisabled}
                style={mentionInputStyle}
                a11ySuggestionsListLabel="Suggested mentions"
              >
                <Mention data={mentionsData} style={mentionStyle} />
              </MentionsInput>
              {Boolean(touched.text && errors.text) ? (
                <Typography variant="caption" color="error" style={{ textAlign: 'left', fontSize: 'var(--fs-sm)' }}>
                  {translate(errors.text)}
                </Typography>
              ) : (
                ''
              )}

                  {files.length > 0 && (
                    <Stack direction="row" spacing={`calc(var(--sp-xxs) * 2)`} flexWrap="wrap">
                      {files.map((item, idx) => {
                        const contentType = item?.file?.type || item?.["content-type"] || '';
                        const isImage = contentType?.startsWith('image/');
                        const uploading = Boolean(item?.uploading);
                        const fileName = item?.file?.name || item?.file_name || (item?.path ? item.path.split('/').pop() : `file-${idx}`);
                        const key = item?.path || item?.id || `${fileName}-${idx}`;

                        return (
                        <Box key={key}>
                          <Box
                            sx={{
                              position: 'relative',
                              width: 'calc(var(--fs-xl)*4)',
                              height: 'calc(var(--fs-xl)*4)',
                              borderRadius: 1,
                              overflow: 'hidden',
                              border: '1px solid #e0e0e0',
                              '&:hover .overlay': { opacity: 1 },
                              '&:hover img': { filter: 'blur(2px) brightness(0.85)' },
                            }}
                          >
                            {isImage ? (
                              (item?.previewUrl || item?.url) ? (
                                <Box
                                  component="img"
                                  src={item.previewUrl || item.url}
                                  alt={fileName}
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover',
                                    transition: 'filter 150ms ease',
                                    filter: uploading ? 'blur(2px) brightness(0.85)' : 'none',
                                  }}
                                />
                                ) : (
                                <Box
                                  sx={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                  }}
                                >
                                  <ImageIcon sx={{ fontSize: `calc(var(--fs-lg)*2)`, color: '#bdbdbd' }} />
                                </Box>
                              )
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  width: '100%',
                                  height: '100%',
                                  backgroundColor: '#f5f5f5',
                                  filter: uploading ? 'blur(2px) brightness(0.85)' : 'none',
                                }}
                              >
                                <PictureAsPdfIcon sx={{ fontSize: 'calc(var(--fs-lg)*2)', color: '#d32f2f' }} />
                              </Box>
                            )}
                            {uploading && (
                              <Box
                                className="overlay"
                                sx={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  backgroundColor: 'rgba(0,0,0,0.6)',
                                }}
                              >
                                <CustomSpinner size={resolveCssVarValue('--fs-xl')*1.32075} />
                              </Box>
                            )}
                            {!uploading && <Box
                              className="overlay"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                display: composerDisabled ? 'none' : 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.35)',
                                opacity: 0,
                                transition: 'opacity 150ms ease',
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => removeFileAt(idx)}
                                aria-label="remove file"
                                sx={{
                                  width: 'calc(var(--fs-xl)*1.6)',
                                  height: 'calc(var(--fs-xl)*1.6)',
                                  borderRadius: '50%',
                                  backgroundColor: '#fff',
                                  color: 'error.main',
                                  boxShadow: '0 0 0 2px rgba(255,255,255,0.9)',
                                  '&:hover': { backgroundColor: '#fff' },
                                }}
                              >
                                <DeleteOutlineIcon fontSize="small" sx={{ color: 'error.main', fontSize: 'var(--fs-xl)' }} />
                              </IconButton>
                            </Box>}
                          </Box>
                          {!isImage && (
                            <Tooltip title={fileName}>
                              <Typography
                                variant="caption"
                                noWrap
                                sx={{ display: 'block', width: '100%', textAlign: 'center', mt: 0.5, fontSize: 'var(--fs-sm)' }}
                              >
                                {fileName}
                              </Typography>
                            </Tooltip>
                          )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}

              <Stack direction={isMobile ? "column" : "row"} alignItems={isMobile ? "stretch" : "center"} justifyContent="space-between" spacing={isMobile ? 1 : 0}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/pdf"
                    multiple
                    style={{ display: 'none' }}
                    onChange={handlePickFiles}
                  />
                  <Tooltip title={disableUpload ? "Select Customer PO first" : ""}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: '#fff',
                          color: Colors.orange,
                          fontWeight: 600,
                          fontSize: 'var(--fs-md)',
                          boxShadow: 2,
                          border: `1px solid ${Colors.orange}`,
                        }
                      }
                    }}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={disableUpload || composerDisabled}
                        sx={{
                          cursor: disableUpload || composerDisabled ? 'not-allowed' : 'pointer',
                          backgroundColor: disableUpload || composerDisabled ? '#f5f5f5' : 'inherit',
                        }}
                      >
                        <CloudUploadIcon fontSize="small" sx={{
                          color: disableUpload || composerDisabled ? '#bdbdbd' : Colors.orange,
                          fontSize: 'calc(var(--fs-xl))',
                        }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary" sx={{fontSize: 'var(--fs-sm)'}}>
                    {unlimited ? `${files.length} files` : `${files.length}/${maxImages} files`}
                  </Typography>
                </Stack>
                <SendButton
                  type="submit"
                  disabled={composerDisabled || processing || anyUploading}
                  sx={{
                    textTransform: 'none',
                    display: composerDisabled ? 'none' : undefined,
                    opacity: (anyUploading || processing) ? 0.5 : 1,
                    cursor: (anyUploading || processing) ? 'not-allowed' : 'pointer',
                  }}
                > {isSubmitting ? (
                    <CustomSpinner size={resolveCssVarValue('--fs-xl')*1.4150943} />
                  ) : (
                    translate(btnText)
                  )}
                </SendButton>
              </Stack>
            </Stack>
            {processing && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'rgba(255,255,255,0.7)',
                  zIndex: 2,
                }}
              >
                <Stack spacing={1} alignItems={'center'}>
                  <CustomSpinner size={resolveCssVarValue('--fs-xl')*1.698113} />
                  <Typography variant="caption" color="text.secondary" sx={{fontSize:'var(--fs-xs)'}}>{translate('uploadingImages')}</Typography>
                </Stack>
              </Box>
            )}
          </ComposerWrapper>
        </Form>
      )}
    </Formik>
  );
}
